/**
 * Payroll inputs — everything a `PayrollRun` needs collected before it can be computed (see
 * `PayrollRun` in `payroll-calendar.contract.ts`, the record every route below locks against).
 *
 * NEVER A SECOND MASTER. Attendance stays owned by the attendance module, leave by the leave
 * module, expense claims by the expense module — this contract only snapshots (attendance),
 * reserves (reimbursements), or records new one-time facts that genuinely don't exist anywhere else
 * yet (variable pay). `syncAttendance` reads `AttendanceDay`/`AttendanceRegularisation` and writes a
 * run-scoped copy; it never becomes a second place attendance corrections get made.
 *
 * FOUR STATES, NOT ONE. Per `PayrollRun`: attendance source freeze, payroll input lock, payroll
 * approval, and payment completion are four separate facts. Everything here only ever touches the
 * first two — freezing attendance stops `syncAttendance` from picking up new corrections, locking
 * inputs stops variable pay and reimbursement inclusion from changing. A locked run can still be
 * reopened (`payroll-calendar.contract.ts`'s `reopenRun`), which unwinds exactly one stage.
 *
 * DOUBLE-PAYMENT GUARDS. `PAYROLL_EXPENSE_INCLUSION_STATUSES` exists so an approved claim can be
 * reserved onto at most one run at a time — `includeClaim` must fail if the claim is already
 * reserved elsewhere, or already paid directly. `overtimeSourceReference` on a variable-pay input
 * exists so a manual overtime entry can be checked against attendance-derived overtime instead of
 * silently stacking on top of it.
 *
 * Implemented by `apps/api/src/routes/payroll-inputs.ts`.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  currencySchema,
  dateOnlySchema,
  decimalStringSchema,
  errorBodySchema,
  idempotencyKeySchema,
  instantSchema,
  paginatedSchema,
  paginationQuerySchema,
  payrollExpenseInclusionStatusSchema,
  payrollVariableInputCategorySchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';
import { payrollRunStateSchema } from './payroll-runs.contract.js';

const c = initContract();
const idempotencyHeadersSchema = z.object({ 'idempotency-key': idempotencyKeySchema });
const runParams = z.object({ runId: uuidSchema });

/** A whole or half-day count — zero is valid here (0 LOP days is the common case), unlike
 * `leaveDaysSchema` which requires a positive amount. */
const dayCountSchema = z.string().regex(/^\d{1,3}(\.5)?$/, 'Must be a whole or half-day count');

const currencyAmountSchema = z.object({ currency: currencySchema, amount: decimalStringSchema });

// ---------------------------------------------------------------------------
// Attendance snapshot
// ---------------------------------------------------------------------------

export const payrollAttendanceSnapshotRowSchema = z.object({
  employmentId: uuidSchema,
  employeeName: z.string(),
  employeeCode: z.string().nullable(),
  /** Calendar length of the payroll period — separate from any salary-proration denominator. */
  windowDays: z.number().int().positive(),
  workingDays: dayCountSchema,
  paidDays: dayCountSchema,
  lopDays: dayCountSchema,
  /** The part of `lopDays` that is the late-mark penalty (0 when none, or the snapshot predates it). */
  latePenaltyDays: decimalStringSchema,
  /** Working dates with no attendance record at all — shown, never silently counted either way. */
  unrecordedDays: dayCountSchema,
  /** Overtime hours as RECORDED by the attendance module; it has no separate approval flag, so this
   * is not "approved" in any stronger sense than the attendance day rows themselves. */
  approvedOvertimeHours: decimalStringSchema.nullable(),
  /** References into the attendance module's own records — never a copy of their content. */
  sourceAttendanceDayIds: z.array(uuidSchema),
});
export type PayrollAttendanceSnapshotRow = z.infer<typeof payrollAttendanceSnapshotRowSchema>;

export const payrollAttendanceSnapshotSchema = z.object({
  id: uuidSchema,
  runId: uuidSchema,
  syncedAt: instantSchema,
  syncedByName: z.string().nullable(),
  /** Increments on every resync — the version this snapshot's rows were read at, so a stale read
   * is visible rather than silently overwritten. */
  sourceVersion: z.number().int().positive(),
  rows: z.array(payrollAttendanceSnapshotRowSchema),
  /**
   * Attendance or leave for these people in this period changed after the snapshot was taken (a
   * regularisation, an approved or cancelled leave): the preview is out of date until re-synced.
   * The pay calculation itself always reads the live data. Absent where it was not checked.
   */
  changedSinceSync: z.boolean().optional(),
});
export type PayrollAttendanceSnapshot = z.infer<typeof payrollAttendanceSnapshotSchema>;

export const attendanceBreakdownDaySchema = z.object({
  date: dateOnlySchema,
  /** The attendance module's own day status label (present/absent/leave/holiday/weekly_off/…) —
   * read, never redefined here. */
  status: z.string(),
  note: z.string().nullable(),
});

export const attendanceBreakdownSchema = z.object({
  employmentId: uuidSchema,
  runId: uuidSchema,
  days: z.array(attendanceBreakdownDaySchema),
});
export type AttendanceBreakdown = z.infer<typeof attendanceBreakdownSchema>;

// ---------------------------------------------------------------------------
// Joiners & exits
// ---------------------------------------------------------------------------

export const payrollJoinerExitRowSchema = z.object({
  employmentId: uuidSchema,
  employeeName: z.string(),
  employeeCode: z.string().nullable(),
  joiningDate: dateOnlySchema.nullable(),
  exitDate: dateOnlySchema.nullable(),
  eligibleDaysInPeriod: z.number().int().nonnegative(),
  payrollProfileReady: z.boolean(),
  /** True when this person's exit is being paid through Full & Final instead — regular payroll must
   * never also pay their exit-period component for the same days. */
  isFnfLinked: z.boolean(),
});
export type PayrollJoinerExitRow = z.infer<typeof payrollJoinerExitRowSchema>;

// ---------------------------------------------------------------------------
// Variable pay
// ---------------------------------------------------------------------------

export const payrollVariableInputSchema = z.object({
  id: uuidSchema,
  runId: uuidSchema,
  employmentId: uuidSchema,
  employeeName: z.string(),
  category: payrollVariableInputCategorySchema,
  amount: decimalStringSchema,
  currency: currencySchema,
  remarks: z.string(),
  createdByName: z.string(),
  createdAt: instantSchema,
  /** Set on an `overtime` entry to reference the attendance-derived overtime hours it corresponds
   * to, if any — checked so the same hours are never paid twice through both paths. */
  overtimeSourceReference: z.string().nullable(),
  version: z.number().int().positive(),
});
export type PayrollVariableInput = z.infer<typeof payrollVariableInputSchema>;

export const createVariableInputBodySchema = z.object({
  employmentId: uuidSchema,
  category: payrollVariableInputCategorySchema,
  amount: decimalStringSchema,
  currency: currencySchema,
  remarks: z.string().trim().min(1).max(500),
  overtimeSourceReference: z.string().trim().max(120).optional(),
});

export const updateVariableInputBodySchema = z.object({
  expectedVersion: z.number().int().positive(),
  amount: decimalStringSchema.optional(),
  remarks: z.string().trim().min(1).max(500).optional(),
});

// ---------------------------------------------------------------------------
// Reimbursement inclusion
// ---------------------------------------------------------------------------

export const payrollExpenseInclusionSchema = z.object({
  id: uuidSchema,
  runId: uuidSchema,
  expenseClaimId: uuidSchema,
  employmentId: uuidSchema,
  employeeName: z.string(),
  amount: decimalStringSchema,
  currency: currencySchema,
  status: payrollExpenseInclusionStatusSchema,
  reservedAt: instantSchema,
  reservedByName: z.string(),
  releasedAt: instantSchema.nullable(),
});
export type PayrollExpenseInclusion = z.infer<typeof payrollExpenseInclusionSchema>;

/** An approved claim that could still be reserved onto this run. */
export const payrollEligibleClaimSchema = z.object({
  id: uuidSchema,
  reference: z.string().nullable(),
  employmentId: uuidSchema,
  employeeName: z.string(),
  categoryName: z.string(),
  merchantName: z.string(),
  expenseDate: dateOnlySchema,
  amount: decimalStringSchema,
  currency: currencySchema,
});
export type PayrollEligibleClaim = z.infer<typeof payrollEligibleClaimSchema>;

export const listEligibleClaimsQuerySchema = paginationQuerySchema.extend({
  employmentId: uuidSchema.optional(),
});

export const includeClaimBodySchema = z.object({
  expenseClaimId: uuidSchema,
});

// ---------------------------------------------------------------------------
// Summary
// ---------------------------------------------------------------------------

export const payrollInputSummarySchema = z.object({
  runId: uuidSchema,
  runStatus: payrollRunStateSchema,
  employeeCount: z.number().int().nonnegative(),
  totalLopDays: decimalStringSchema,
  joinersCount: z.number().int().nonnegative(),
  exitsCount: z.number().int().nonnegative(),
  variableInputsCount: z.number().int().nonnegative(),
  variableInputsTotal: z.array(currencyAmountSchema),
  reimbursementInclusionsCount: z.number().int().nonnegative(),
  reimbursementInclusionsTotal: z.array(currencyAmountSchema),
  attendanceSyncedAt: instantSchema.nullable(),
  inputsLocked: z.boolean(),
});
export type PayrollInputSummary = z.infer<typeof payrollInputSummarySchema>;

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

const employmentParams = z.object({ runId: uuidSchema, employmentId: uuidSchema });
const variableInputParams = z.object({ runId: uuidSchema, inputId: uuidSchema });
const inclusionParams = z.object({ runId: uuidSchema, inclusionId: uuidSchema });

export const payrollInputsContract = c.router(
  {
    syncAttendance: {
      method: 'POST',
      path: '/runs/:runId/attendance/sync',
      pathParams: runParams,
      headers: idempotencyHeadersSchema,
      body: z.object({}),
      responses: {
        200: payrollAttendanceSnapshotSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** The run's attendance is already frozen. */
        409: errorBodySchema,
      },
      summary: "Read the cycle's attendance/leave/regularisations and refresh this run's snapshot",
      metadata: routeMeta(['payroll:read', 'payroll.input:manage']),
    },

    getAttendanceSnapshot: {
      method: 'GET',
      path: '/runs/:runId/attendance',
      pathParams: runParams,
      responses: {
        200: payrollAttendanceSnapshotSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** No sync has happened for this run yet. */
        404: errorBodySchema,
      },
      summary: "This run's current attendance snapshot",
      metadata: routeMeta('payroll:read'),
    },

    getAttendanceBreakdown: {
      method: 'GET',
      path: '/runs/:runId/attendance/:employmentId/breakdown',
      pathParams: employmentParams,
      responses: {
        200: attendanceBreakdownSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "Day-level attendance explaining one employee's snapshot totals",
      metadata: routeMeta('payroll:read'),
    },

    listJoinersExits: {
      method: 'GET',
      path: '/runs/:runId/joiners-exits',
      pathParams: runParams,
      responses: {
        200: z.object({ items: z.array(payrollJoinerExitRowSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "Employments overlapping this run's period, joined or exited mid-cycle",
      metadata: routeMeta('payroll:read'),
    },

    listVariableInputs: {
      method: 'GET',
      path: '/runs/:runId/variable-inputs',
      pathParams: runParams,
      responses: {
        200: z.object({ items: z.array(payrollVariableInputSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "This run's one-time earnings and deductions",
      metadata: routeMeta('payroll:read', { salaryPin: 'others' }),
    },

    createVariableInput: {
      method: 'POST',
      path: '/runs/:runId/variable-inputs',
      pathParams: runParams,
      headers: idempotencyHeadersSchema,
      body: createVariableInputBodySchema,
      responses: {
        201: payrollVariableInputSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Inputs are locked for this run. */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Add a one-time earning or deduction to this run',
      metadata: routeMeta(['payroll:read', 'payroll.input:manage'], { salaryPin: 'others' }),
    },

    updateVariableInput: {
      method: 'PATCH',
      path: '/runs/:runId/variable-inputs/:inputId',
      pathParams: variableInputParams,
      body: updateVariableInputBodySchema,
      responses: {
        200: payrollVariableInputSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Edit a variable-pay input while its run is still unlocked',
      metadata: routeMeta(['payroll:read', 'payroll.input:manage'], { salaryPin: 'others' }),
    },

    deleteVariableInput: {
      method: 'DELETE',
      path: '/runs/:runId/variable-inputs/:inputId',
      pathParams: variableInputParams,
      responses: {
        204: z.void(),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Remove a variable-pay input while its run is still unlocked',
      metadata: routeMeta(['payroll:read', 'payroll.input:manage']),
    },

    listEligibleClaims: {
      method: 'GET',
      path: '/runs/:runId/reimbursements/eligible',
      pathParams: runParams,
      query: listEligibleClaimsQuerySchema,
      responses: {
        200: paginatedSchema(payrollEligibleClaimSchema),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Approved claims not yet reserved onto any run and not paid directly',
      metadata: routeMeta('payroll:read'),
    },

    listIncludedClaims: {
      method: 'GET',
      path: '/runs/:runId/reimbursements',
      pathParams: runParams,
      responses: {
        200: z.object({ items: z.array(payrollExpenseInclusionSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Claims reserved onto this run',
      metadata: routeMeta('payroll:read'),
    },

    includeClaim: {
      method: 'POST',
      path: '/runs/:runId/reimbursements',
      pathParams: runParams,
      headers: idempotencyHeadersSchema,
      body: includeClaimBodySchema,
      responses: {
        201: payrollExpenseInclusionSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Inputs are locked, or the claim is already reserved elsewhere or already paid directly —
         * the same settlement service the expense module uses is what rules on this. */
        409: errorBodySchema,
      },
      summary: 'Reserve an approved claim onto this run',
      metadata: routeMeta(['payroll:read', 'payroll.input:manage']),
    },

    releaseClaimInclusion: {
      method: 'POST',
      path: '/runs/:runId/reimbursements/:inclusionId/release',
      pathParams: inclusionParams,
      headers: idempotencyHeadersSchema,
      body: z.object({ reason: z.string().trim().max(500).optional() }),
      responses: {
        200: payrollExpenseInclusionSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Already `settled` — a paid inclusion can never be released. */
        409: errorBodySchema,
      },
      summary: 'Release a reservation — automatic when an eligible run is cancelled, or manual',
      metadata: routeMeta(['payroll:read', 'payroll.input:manage']),
    },

    getInputSummary: {
      method: 'GET',
      path: '/runs/:runId/summary',
      pathParams: runParams,
      responses: {
        200: payrollInputSummarySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Aggregate view of everything staged for this run',
      metadata: routeMeta('payroll:read', { salaryPin: 'others' }),
    },
  },
  { pathPrefix: '/payroll/inputs' },
);
